/**
 * sessionStorage-backed, user-scoped, TTL-based draft store for
 * NON-SENSITIVE work-product UI state.
 *
 * This is the persistence companion to src/lib/cache.js — cache.js is
 * in-memory ONLY by explicit security contract and must never be used for
 * reload persistence. The draft store exists for exactly one purpose:
 * keeping expensive-to-regenerate results (e.g. AI suggestions the user
 * waited seconds for) and meaningful unsaved user work-product (e.g. a code
 * editor buffer) alive across a page reload or component unmount.
 *
 * Security contract:
 *
 *  1. sessionStorage ONLY — per-tab, origin-scoped, survives a reload but
 *     NOT a tab close. Never localStorage, never cookies, never IndexedDB.
 *
 *  2. Every key MUST be scoped to the authenticated user via
 *     draftKey(user, resource) — the same pattern as cache.js's
 *     userCacheKey. A null key means "do not persist"; every draftGet /
 *     draftSet / draftDelete call silently no-ops on null keys.
 *
 *  3. NEVER store authentication tokens, credentials, PII, or cached API
 *     responses here. Only draft work-product the user authored in the UI,
 *     or AI-suggestion results already rendered on screen. If in doubt,
 *     do not persist it.
 *
 *  4. draftClearAll() MUST be called on every code path that calls
 *     cacheClearAll() (logout, session expiry, login/user-switch, license
 *     lockout). The two clears always travel together so a user switch in
 *     the same tab can never leak one user's drafts to another.
 *
 *  5. Values are JSON-serialized with a written-at timestamp and a
 *     per-entry TTL (default 30 minutes). Reads drop expired or corrupted
 *     entries on sight.
 *
 *  6. ALL sessionStorage access is wrapped in try/catch — storage can be
 *     unavailable (privacy modes, disabled storage) or full (quota).
 *     Every failure degrades silently to a no-op; the draft store is a
 *     convenience layer, never a source of truth.
 */

/** Namespace prefix so draftClearAll() can find our keys without touching
 *  anything else that may live in sessionStorage. */
const PREFIX = "tozen:draft:";

/** Default entry lifetime: 30 minutes. */
export const DRAFT_DEFAULT_TTL_MS = 30 * 60_000;

interface DraftEntry {
  data: unknown;
  writtenAt: number;
  expiresAt: number;
}

interface DraftUser {
  id?: string | number | null;
  email?: string | null;
}

/**
 * Build a user-scoped draft key. Mirrors userCacheKey in src/lib/cache.js:
 * returns null when the user identity cannot be determined — callers treat
 * null as "do not persist". Uses the numeric DB id when available,
 * otherwise falls back to the email address.
 */
export function draftKey(
  user: DraftUser | null | undefined,
  resource: string,
): string | null {
  if (!user || !resource) return null;

  const namespace =
    user.id != null && String(user.id).length > 0
      ? String(user.id)
      : user.email || null;

  if (!namespace) return null;

  return `${namespace}:${resource}`;
}

function storageKey(key: string): string {
  return `${PREFIX}${key}`;
}

function removeSilently(fullKey: string): void {
  try {
    sessionStorage.removeItem(fullKey);
  } catch {
    // Storage unavailable — nothing to clean up.
  }
}

/**
 * Retrieve a stored draft.
 *
 * Returns null on: null key, miss, expired entry, corrupted JSON, or
 * unavailable storage. Expired/corrupted entries are deleted on read.
 */
export function draftGet<T = unknown>(key: string | null | undefined): T | null {
  if (!key) return null;

  const fullKey = storageKey(key);
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(fullKey);
  } catch {
    return null;
  }
  if (raw == null) return null;

  let entry: DraftEntry;
  try {
    entry = JSON.parse(raw) as DraftEntry;
  } catch {
    removeSilently(fullKey);
    return null;
  }

  if (
    entry == null ||
    typeof entry !== "object" ||
    typeof entry.expiresAt !== "number"
  ) {
    removeSilently(fullKey);
    return null;
  }

  if (Date.now() > entry.expiresAt) {
    removeSilently(fullKey);
    return null;
  }

  return entry.data as T;
}

/**
 * Store a draft under key with a TTL (default 30 minutes).
 * No-ops silently on null key, non-positive TTL, unserializable values,
 * or unavailable/full storage.
 */
export function draftSet(
  key: string | null | undefined,
  data: unknown,
  ttlMs: number = DRAFT_DEFAULT_TTL_MS,
): void {
  if (!key || ttlMs <= 0) return;

  const now = Date.now();
  const entry: DraftEntry = { data, writtenAt: now, expiresAt: now + ttlMs };
  try {
    sessionStorage.setItem(storageKey(key), JSON.stringify(entry));
  } catch {
    // Quota exceeded or storage unavailable — fail silent by contract.
  }
}

/** Delete a single draft. No-ops on null key or unavailable storage. */
export function draftDelete(key: string | null | undefined): void {
  if (!key) return;
  removeSilently(storageKey(key));
}

/**
 * Purge every draft-store entry (only keys under our prefix).
 *
 * MUST be called wherever cacheClearAll() is called — logout, session
 * expiry, login/user-switch, license lockout — so drafts never survive an
 * identity transition within the same tab.
 */
export function draftClearAll(): void {
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(PREFIX)) toRemove.push(k);
    }
    for (const k of toRemove) sessionStorage.removeItem(k);
  } catch {
    // Storage unavailable — nothing persisted, nothing to clear.
  }
}
